import type { AICXConfig } from '../types/config';

/** DOM ID 상수 */
export const CONTAINER_ID = 'aicx-container';
export const IFRAME_ID = 'aicx-iframe';

/** 디자인 시스템 상수 */
const DESIGN_TOKENS = {
  Z_INDEX: 999999,
  BORDER_RADIUS: 16,
  SHADOW: '0 4px 24px rgba(0, 0, 0, 0.15)',
  MARGIN: 20,
  DEFAULT_WIDTH: 400,
  DEFAULT_HEIGHT: 600,
  BG_COLOR: '#ffffff',
  TRANSITION: 'opacity 0.2s ease, transform 0.2s ease',
};

/** 기본 설정 */
const DEFAULTS = {
  position: 'bottom-right' as const,
  displayMode: 'full' as const,
};

/**
 * 위치에 따른 CSS 속성 반환 (Floating 모드용)
 */
function getPositionStyles(position: AICXConfig['position']): Partial<CSSStyleDeclaration> {
  const margin = `${DESIGN_TOKENS.MARGIN}px`;

  const positions: Record<string, Partial<CSSStyleDeclaration>> = {
    'bottom-left': { bottom: margin, left: margin },
    'top-right': { top: margin, right: margin },
    'top-left': { top: margin, left: margin },
    'bottom-right': { bottom: margin, right: margin },
  };

  const style = positions[position || DEFAULTS.position];
  return style || positions['bottom-right']!;
}

/**
 * 컨테이너 스타일 생성
 */
export function getContainerStyles(config: AICXConfig): Partial<CSSStyleDeclaration> {
  const displayMode = config.displayMode ?? DEFAULTS.displayMode;
  const zIndex = String(config.zIndex ?? DESIGN_TOKENS.Z_INDEX);

  // 1. Full Page 모드
  if (displayMode === 'full') {
    const isEmbedded = !!config.containerId;

    return isEmbedded
      ? {
          position: 'relative',
          width: '100%',
          height: '100%',
          backgroundColor: DESIGN_TOKENS.BG_COLOR,
          border: 'none',
          overflow: 'hidden',
        }
      : {
          position: 'fixed',
          top: '0',
          left: '0',
          width: '100%',
          height: '100%',
          zIndex,
          backgroundColor: DESIGN_TOKENS.BG_COLOR,
          border: 'none',
          overflow: 'hidden',
        };
  }

  // 2. Floating 모드
  const position = config.position ?? DEFAULTS.position;
  return {
    position: 'fixed',
    ...getPositionStyles(position),
    width: `${config.width ?? DESIGN_TOKENS.DEFAULT_WIDTH}px`,
    height: `${config.height ?? DESIGN_TOKENS.DEFAULT_HEIGHT}px`,
    zIndex,
    borderRadius: `${DESIGN_TOKENS.BORDER_RADIUS}px`,
    boxShadow: DESIGN_TOKENS.SHADOW,
    overflow: 'hidden',
    transition: DESIGN_TOKENS.TRANSITION,
    backgroundColor: DESIGN_TOKENS.BG_COLOR,
  };
}

/**
 * iframe 스타일 생성
 */
export function getIframeStyles(config: AICXConfig): Partial<CSSStyleDeclaration> {
  const displayMode = config.displayMode ?? DEFAULTS.displayMode;

  return {
    width: '100%',
    height: '100%',
    border: 'none',
    borderRadius: displayMode === 'full' ? '0' : `${DESIGN_TOKENS.BORDER_RADIUS}px`,
    backgroundColor: 'transparent',
  };
}
